import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { TypedMemo } from '../../../../../common/types';
import type { ReelsPlanPreview } from '../../../../../../redux/api/fymAPI.types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import type { VideoCreditGate } from '../../../credits/useVideoCreditGate';
import { StudioActionError } from '../../../studio/StudioActionError';
import type { StudioActionFeedback } from '../../../studio/studioActionErrorText';
import { reelsPlanMessages } from './reelsPlanMessages';
import {
  reelsPlanFootKind,
  reelsPlanMissingCredits,
  reelsPlanScheduledLine,
  reelsPlanShowsRepeatNote,
  reelsPlanSummary,
} from './reelsPlanModel';
import { ReelsPlanRefusalLine } from './ReelsPlanRefusalLine';
import { ReelsPlanSummaryLine } from './ReelsPlanSummaryLine';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the AI reels plan; each part uses its own slice of it */
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanFootProps {
  catalogItemUuid: string;
  songTitle: string;
  /** The dry run of the settings on the screen; undefined while there is none. */
  preview: ReelsPlanPreview | undefined;
  isChecking: boolean;
  hasFailed: boolean;
  /** The sentence for settings that the server would refuse (the length); null for good settings. */
  invalidNote: string | null;
  /** False for the demo artist and a read-only artist. */
  canAct: boolean;
  gate: Pick<VideoCreditGate, 'offerCredits' | 'shortLabel'>;
  isMaking: boolean;
  feedback: StudioActionFeedback | null;
  onMake: () => void;
  /**
   * The limit upsell of the setup (design limit-upsell a1, a6). It takes the place of the line of
   * the posts left and of the credit notes, which say the same numbers.
   */
  upsell?: React.ReactNode;
}

/**
 * The foot of the setup (design rollout-plan f1, f2): the summary line of the dry run, the balance
 * and "Make the plan", and the scheduled posts left this month when the plan has a limit (that line
 * never stops the campaign). A short balance or missing credits turn the button into the credit
 * offer, and the other refusals take the place of the line (`ReelsPlanRefusalLine`). A failed click
 * shows its sentence under the button.
 */
const ReelsPlanFootComponent: React.FC<ReelsPlanFootProps> = ({
  catalogItemUuid,
  songTitle,
  preview,
  isChecking,
  hasFailed,
  invalidNote,
  canAct,
  gate,
  isMaking,
  feedback,
  onMake,
  upsell,
}) => {
  const intl = useIntl();
  const isUsable = invalidNote === null && !hasFailed;
  const kind = isUsable ? reelsPlanFootKind(preview) : 'checking';
  const summary = isUsable ? reelsPlanSummary(preview) : null;
  const missingCredits = reelsPlanMissingCredits(preview);
  const scheduled = isUsable && upsell === undefined ? reelsPlanScheduledLine(preview) : null;

  if (kind !== 'checking' && kind !== 'ready' && kind !== 'notEnoughCredits') {
    return <ReelsPlanRefusalLine refusal={kind} catalogItemUuid={catalogItemUuid} songTitle={songTitle} />;
  }

  const isShort = kind === 'notEnoughCredits';
  let note: { text: string; tone: string | undefined } | null = null;
  if (invalidNote !== null) {
    note = { text: invalidNote, tone: undefined };
  } else if (hasFailed) {
    note = { text: intl.formatMessage(reelsPlanMessages.previewFailed), tone: styles.noteBad };
  } else if (isShort && upsell !== undefined) {
    note = null;
  } else if (isShort && missingCredits > 0) {
    note = {
      text: intl.formatMessage(reelsPlanMessages.noteBuyMore, { missing: missingCredits }),
      tone: styles.noteBad,
    };
  } else if (isShort && summary !== null) {
    note = {
      text: intl.formatMessage(reelsPlanMessages.noteShort, { cost: summary.credits, left: summary.creditsLeft ?? 0 }),
      tone: styles.noteBad,
    };
  } else if (reelsPlanShowsRepeatNote(preview)) {
    note = {
      text: intl.formatMessage(reelsPlanMessages.noteRepeat, { parts: preview?.hookCandidates ?? 0 }),
      tone: styles.noteWarn,
    };
  }

  return (
    <>
      <div className={styles.foot}>
        {summary !== null && <ReelsPlanSummaryLine summary={summary} />}
        <span className={styles.grow} />
        {summary !== null && summary.creditsLeft !== null && (
          <span className={styles.have}>
            {intl.formatMessage(reelsPlanMessages.have, { left: summary.creditsLeft })}
          </span>
        )}
        {isShort ? (
          <SMLButton variant="primary" className={styles.action} disabled={!canAct} onClick={gate.offerCredits}>
            {gate.shortLabel}
          </SMLButton>
        ) : (
          <SMLButton
            variant="primary"
            className={styles.action}
            icon={<FontAwesomeIcon icon={faSparkles} />}
            disabled={!canAct || kind !== 'ready' || isChecking || isMaking}
            onClick={onMake}
          >
            {intl.formatMessage(reelsPlanMessages.make)}
          </SMLButton>
        )}
      </div>
      {isUsable && upsell !== undefined && <div className={styles.feedback}>{upsell}</div>}
      {scheduled !== null && (
        <p className={styles.note} data-testid="reels-plan-scheduled">
          {intl.formatMessage(reelsPlanMessages.scheduledLeft, scheduled)}
        </p>
      )}
      {note !== null && (
        <p className={clsx(styles.note, note.tone)} data-testid="reels-plan-note">
          {note.text}
        </p>
      )}
      {feedback !== null && (
        <div className={styles.feedback}>
          <StudioActionError feedback={feedback} />
        </div>
      )}
    </>
  );
};

export const ReelsPlanFoot = TypedMemo(ReelsPlanFootComponent);
